import {
    type Availability,
    type Department,
    type TimeEntry,
    type Venue,
    overlap,
    parseTimes,
    toDateString,
    toTimeString,
    wait,
} from "@pentacode/core";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { app } from "../init";
import { mixins, shared } from "../styles";

export const entryPositions = new Map<string, { x: number; y: number }>();

@customElement("ptc-roster-entry")
export class RosterEntry extends LitElement {
    @property({ attribute: false })
    entry: TimeEntry;

    @property({ reflect: true, type: Boolean })
    error: boolean = false;

    @property({ attribute: false })
    department: Department;

    @property({ attribute: false })
    venue: Venue;

    @property({ type: Boolean, reflect: true })
    readonly: boolean = false;

    @property({ type: Boolean })
    displayPublished: boolean = false;

    @property()
    color?: string;

    @property({ type: Number })
    stackSize?: number;

    @property({ type: String, reflect: true })
    layout: "regular" | "compact" | "minimal" = "regular";

    @property({ type: Boolean })
    animated = false;

    @property({ attribute: false })
    availabilities?: Availability[] = [];

    private _fireRemove(e: Event) {
        e.stopPropagation();
        this.dispatchEvent(new CustomEvent("remove", { detail: { entry: this.entry }, bubbles: true, composed: true }));
    }

    private _dispatchSelect(field: string, e: Event) {
        const otherDepartment =
            this.entry.position && this.department && this.entry.position.departmentId !== this.department.id;
        if (!otherDepartment) {
            this.dispatchEvent(
                new CustomEvent("select", { detail: { entry: this.entry.id, field }, bubbles: true, composed: true })
            );
            e.stopPropagation();
            e.preventDefault();
        }
    }

    // FLIP batching: each entry reads its post-render rect to animate from its old
    // position. Reading getBoundingClientRect interleaved with the transform writes
    // below forces one synchronous layout per entry — N reflows for N entries. Queue
    // the entries instead and, on the next frame, read every rect first (one layout
    // for the whole batch) before applying any transform.
    private static _flipQueue = new Set<RosterEntry>();
    private static _flipScheduled = false;

    private static _scheduleFlip(entry: RosterEntry) {
        RosterEntry._flipQueue.add(entry);
        if (RosterEntry._flipScheduled) {
            return;
        }
        RosterEntry._flipScheduled = true;
        requestAnimationFrame(() => {
            RosterEntry._flipScheduled = false;
            const batch = [...RosterEntry._flipQueue].filter((e) => e.isConnected);
            RosterEntry._flipQueue.clear();
            // Phase 1 — read all rects (single forced layout for the whole batch)
            const measured = batch.map((e) => ({ entry: e, after: e.getBoundingClientRect() }));
            // Phase 2 — writes only, no reads, so layout is not forced again
            for (const { entry, after } of measured) {
                void entry._applyFlip(after);
            }
        });
    }

    private async _applyFlip(after: DOMRect) {
        const id = `${this.department?.id}_${this.entry.id}`;
        const before = entryPositions.get(id);
        entryPositions.set(id, { x: after.x, y: after.y });
        if (before && (before.x !== after.x || before.y !== after.y)) {
            this.style.transition = "";
            this.style.transform = `translate(${before.x - after.x}px, ${before.y - after.y}px)`;
            await wait(Math.random() * 100 + 10);
            this.style.zIndex = "10";
            this.style.opacity = "1";
            this.style.transition = "transform 0.5s";
            this.style.transform = "";
            await wait(500);
            this.style.position = "";
            this.style.zIndex = "";
        } else {
            this.style.opacity = "1";
        }
    }

    updated() {
        if (this.animated) {
            RosterEntry._scheduleFlip(this);
        }
    }

    static styles = [
        shared,
        css`
            :host {
                border-radius: var(--border-radius);
                display: block;
                cursor: pointer;
                position: relative;
            }

            .container {
                box-sizing: border-box;
                position: relative;
                overflow: hidden;
                border-radius: var(--border-radius);
                border: solid 1px;
                color: var(--color-highlight);
                z-index: 1;
                background: var(--color-bg);
                height: 100%;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
            }

            :host(.selected) .container {
                background: var(--color-highlight);
                color: var(--color-bg);
            }

            :host([readonly]) .container {
                pointer-events: none;
            }

            .container:hover .status {
                display: none;
            }

            .delete-button {
                background: transparent;
                color: inherit;
                display: grid;
                place-content: center;
                padding: 0;
                aspect-ratio: 1;
                line-height: 1.1;
                border-radius: 2.5px;

                ptc-ui-icon {
                    line-height: inherit;
                }
            }

            .container:not(:hover) .delete-button {
                display: none;
            }

            .dash {
                font-size: var(--font-size-smaller);
                vertical-align: middle;
                font-weight: 900;
                position: relative;
                margin: 0 -0.1em;
                z-index: 3;
            }

            .type {
                text-align: center;
                padding: 0.69em;
                font-size: var(--font-size-huger);
            }

            .status {
                font-weight: 600;
            }

            .time {
                font-size: 1.55em;
                margin: 0 0.1em 0.1em 0.1em;
            }

            .time > button {
                padding: 0 0.2em;
                letter-spacing: 0.05em;
                font-weight: bold;
                color: inherit;
                border-radius: calc(var(--border-radius) - 5px);
                line-height: 1.25;
            }

            /* The shared button hover/active fills the background via box-shadow: inset … 1000px.
               Use a plain background here instead so the entry buttons paint no box-shadow. */
            .time > button:hover,
            .delete-button:hover {
                box-shadow: none;
                background-color: var(--shade-1);
            }

            .time > button:active,
            .delete-button:active {
                box-shadow: none;
                background-color: var(--shade-2);
            }

            .subtle {
                border: none;
            }

            .subtle * {
                font-weight: normal !important;
            }

            .subtle button {
                pointer-events: none;
                color: inherit;
            }

            .subtle > .status,
            .subtle > .delete-button {
                display: none !important;
            }

            .stack-1,
            .stack-2 {
                border-radius: var(--border-radius);
                border: solid 1px;
                color: var(--color-highlight);
                background: var(--color-bg);
                ${mixins.fullbleed()};
                transition: transform 0.1s;
            }

            .stack-1 {
                transform: scale(0.98) translateY(-2px);
                z-index: -1;
            }

            .stack-2 {
                transform: scale(0.96) translateY(-4px);
                z-index: -2;
            }

            :host(:hover) .stack-1,
            :host(:hover) .stack-2 {
                transform: none;
            }

            :host([layout="compact"]),
            :host([layout="minimal"]) {
                .type {
                    padding: 0.16em;
                }
                .time {
                    margin-top: 0.1em;
                }
            }
        `,
    ];

    render() {
        const a = this.entry;

        const { venue, department } = (a.position && app.getDepartment(a.position.departmentId)) || {
            venue: null,
            department: null,
        };

        const otherVenue = a.position && this.venue && (!venue || venue.id !== this.venue.id);
        const otherDep = a.position && this.department && (!department || department.id !== this.department.id);
        const depName = (department && department.name) || "[Archiv. Pos.]";
        const venueName = (venue && venue.name) || "[Archiv. Pos.]";
        const color = this.color || app.getTimeEntryColor(this.entry) || "lightgray";
        const isPast = this.entry.date < toDateString(new Date());

        const start =
            this.displayPublished && a.published
                ? a.published.startPlanned
                : a.startFinal || a.endFinal
                  ? toTimeString(a.startFinal)
                  : toTimeString(a.startPlanned);
        const end =
            this.displayPublished && a.published
                ? a.published.endPlanned
                : a.endFinal
                  ? toTimeString(a.endFinal)
                  : toTimeString(a.endPlanned);

        const planned = this.entry.planned;

        const availability = this.availabilities?.find((av) => {
            const interval = parseTimes(av.date, av.start || "00:00", av.end || "24:00") as [Date, Date];
            return planned && overlap(planned, [interval]) > 0;
        });

        return a.position
            ? html`
                  <div
                      class="${otherDep ? "subtle" : ""} container"
                      style="--color-highlight: ${color};"
                      @click=${(e: Event) => this._dispatchSelect("start", e)}
                  >
                      <div
                          class="horizontal center-aligning layout"
                          style="margin: 0.4em 0.4em 0 0.6em"
                          ?hidden=${!(this.layout === "regular")}
                      >
                          <div class="semibold stretch ellipsis collapse">
                              ${this.stackSize && this.stackSize > 1 ? html`<strong>${this.stackSize} x </strong>` : ""}
                              ${otherVenue ? venueName : otherDep ? `${depName} / ${a.position.name}` : a.position.name}
                          </div>

                          ${availability
                              ? html`
                                    <div class="status" style="margin-right: 0">
                                        <ptc-ui-icon
                                            name="${availability.icon}"
                                            style="color: ${availability.color}"
                                        ></ptc-ui-icon>
                                    </div>
                                `
                              : ""}

                          <button
                              ?hidden=${this.layout === "minimal"}
                              class="delete-button"
                              @click=${this._fireRemove}
                              tabindex="-1"
                          >
                              <ptc-ui-icon name="icon.action.remove"></ptc-ui-icon>
                          </button>

                          ${this.error
                              ? html`
                                    <div class="issue status">
                                        <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon>
                                    </div>
                                `
                              : !a.position.active
                                ? html`
                                      <div class="issue status">
                                          <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon>
                                      </div>
                                  `
                                : a.preview
                                  ? html`
                                        <div class="status">
                                            <ptc-ui-icon name="icon.action.apply-template"></ptc-ui-icon>
                                        </div>
                                    `
                                  : a.startFinal
                                    ? html`
                                          <div class="status">
                                              ${a.endFinal
                                                  ? html`<ptc-ui-icon name="icon.status.success"></ptc-ui-icon>`
                                                  : html`<ptc-ui-icon name="icon.object.time"></ptc-ui-icon>`}
                                          </div>
                                      `
                                    : a.offered && a.offered > a.updated && (!a.taken || a.offered > a.taken)
                                      ? html`
                                            <div class="status">
                                                <ptc-ui-icon name="icon.action.offer-shift"></ptc-ui-icon>
                                            </div>
                                        `
                                      : a.offered && a.taken && a.taken > a.offered && a.taken > a.updated
                                        ? html`
                                              <div class="status">
                                                  <ptc-ui-icon name="icon.status.swapped"></ptc-ui-icon>
                                              </div>
                                          `
                                        : a.taken && a.taken >= a.updated
                                          ? html`
                                                <div class="status">
                                                    <ptc-ui-icon name="icon.status.approved"></ptc-ui-icon>
                                                </div>
                                            `
                                          : a.isPublished && a.isSeen
                                            ? html`
                                                  <div class="status">
                                                      <ptc-ui-icon name="icon.toggle.visibility"></ptc-ui-icon>
                                                  </div>
                                              `
                                            : a.isPublished
                                              ? html`
                                                    <div class="status">
                                                        <ptc-ui-icon name="icon.action.publish"></ptc-ui-icon>
                                                    </div>
                                                `
                                              : html`
                                                    <div class="status">
                                                        <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                                                    </div>
                                                `}
                      </div>

                      ${this.layout !== "minimal"
                          ? html`<div class="horizontal center-aligning layout time">
                                <button
                                    class="transparent ${isPast && !a.startFinal ? "faded" : ""}"
                                    @click=${(e: Event) => this._dispatchSelect("start", e)}
                                    tabindex="-1"
                                >
                                    ${start || html`<span class="semibold">offen</span>`}
                                </button>

                                <span class="dash">&ndash;</span>

                                <button
                                    class="transparent ${(isPast || a.startFinal) && !a.endFinal ? "faded" : ""}"
                                    @click=${(e: Event) => this._dispatchSelect("end", e)}
                                    tabindex="-1"
                                >
                                    ${end || html`<span class="semibold">offen</span>`}
                                </button>
                            </div>`
                          : html`<div class="type">
                                <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(a.type)}"></ptc-ui-icon>
                            </div>`}
                  </div>
                  ${this.stackSize && this.stackSize > 1
                      ? html` <div class="stack-1" style="--color-highlight: ${color};"></div> `
                      : ""}
                  ${this.stackSize && this.stackSize > 2
                      ? html` <div class="stack-2" style="--color-highlight: ${color};"></div> `
                      : ""}
              `
            : html`
                  <div
                      class="container"
                      style="--color-highlight: ${color}"
                      @click=${(e: Event) => this._dispatchSelect("start", e)}
                  >
                      ${this.layout === "minimal"
                          ? html`<div class="type">
                                <ptc-ui-icon name="${app.localized.timeEntryTypeIcon(a.type)}"></ptc-ui-icon>
                            </div>`
                          : html`<div class="type">${app.localized.timeEntryTypeLabel(a)}</div>`}

                      <button
                          ?hidden=${this.layout === "minimal"}
                          class="delete-button margined absolute"
                          style="top: 0.5em; right: 0.5em;"
                          @click=${this._fireRemove}
                          tabindex="-1"
                      >
                          <ptc-ui-icon name="icon.action.remove"></ptc-ui-icon>
                      </button>

                      ${this.error
                          ? html`
                                <div class="issue status">
                                    <ptc-ui-icon name="icon.status.warning"></ptc-ui-icon>
                                </div>
                            `
                          : ""}
                  </div>
              `;
    }
}
